import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PrepareAppReviewDemoRequest } from "../../features/admin/contracts/PrepareAppReviewDemoRequest";
import ModalShell from "../modal/ModalShell";

const LANGUAGES: { code: string; label: string }[] = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "uk", label: "Українська" },
  { code: "pl", label: "Polski" },
  { code: "ja", label: "日本語" },
  { code: "ar", label: "العربية" },
];

const MIN_PASSWORD_LENGTH = 12;

interface AppReviewDemoModalProps {
  busy: boolean;
  onApply: (request: PrepareAppReviewDemoRequest) => void;
  onClose: () => void;
}

type PasswordKey = "ownerPassword" | "memberPassword" | "guardianPassword";

const PASSWORD_FIELDS: { key: PasswordKey; label: string }[] = [
  { key: "ownerPassword", label: "Owner password" },
  { key: "memberPassword", label: "Member password" },
  { key: "guardianPassword", label: "Guardian password" },
];

/**
 * The confirm sheet of "Prepare App Review demo": the Owner's, the student's and the parent's generated
 * passwords (pasted from ~/.config/timeplanned/app-review.json, sent once, never stored in clear) and the
 * sample language.
 */
const AppReviewDemoModal: React.FC<AppReviewDemoModalProps> = ({ busy, onApply, onClose }) => {
  const { t } = useTranslation();
  const [passwords, setPasswords] = useState<Record<PasswordKey, string>>({
    ownerPassword: "",
    memberPassword: "",
    guardianPassword: "",
  });
  const [locale, setLocale] = useState("en");

  const valid = PASSWORD_FIELDS.every((field) => passwords[field.key].length >= MIN_PASSWORD_LENGTH);

  return (
    <ModalShell onClose={onClose} labelledBy="app-review-demo-title">
      <div className="p-6">
        <h2 id="app-review-demo-title" className="text-lg font-bold text-grey-20 dark:text-white">
          Prepare App Review demo
        </h2>
        <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">
          Creates the demo school, or resets it: the Owner, the sample data, the demo member, the guardian with
          a ward, the three passwords and one conversation. Everything added to the demo school since the last run is replaced.
        </p>

        {PASSWORD_FIELDS.map((field, index) => (
          <label key={field.key} className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
            {field.label}
            <input
              type="password"
              className="input mt-1"
              value={passwords[field.key]}
              onChange={(event) => setPasswords({ ...passwords, [field.key]: event.target.value })}
              autoComplete="new-password"
              autoFocus={index === 0}
            />
          </label>
        ))}

        <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
          Sample language
          <select className="input mt-1" value={locale} onChange={(event) => setLocale(event.target.value)}>
            {LANGUAGES.map((language) => (
              <option key={language.code} value={language.code}>
                {language.label}
              </option>
            ))}
          </select>
        </label>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !valid}
            onClick={() => onApply({ ...passwords, locale })}
          >
            Prepare
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

export default AppReviewDemoModal;
